import * as React from 'react';
import { useCallback, useRef, useState } from 'react';
import { MagnifyingGlassIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import { useOnClickOutside } from 'usehooks-ts';

import { ChipCheckBox, Heading, InputProps, TextInput } from '@restworld/ui-ds-base';
import type { IconComponentType } from '@restworld/ui-ds-base';

import { Locality, loadLocalities, searchLocalities } from './search-localities';

const MIN_SEARCH_LENGTH = 2;

interface LocalitySearchInputProps {
  onSelect: (locality: Locality) => void;
  wrapperStyle?: string;
}

/**
 * The city search of the navbar and of the home page. Same look as GoogleAutoCompleteInput, which
 * stays on the address fields: here the data ships with the app, so searching costs no Google call.
 */
export const LocalitySearchInput = ({ onSelect, wrapperStyle }: LocalitySearchInputProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [results, setResults] = useState<Locality[] | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastQueryRef = useRef('');

  useOnClickOutside(containerRef, () => {
    setResults(null);
    // A first search still waiting for the data must not reopen what the person just closed.
    lastQueryRef.current = '';
  });

  const handleChange = useCallback(async (query: string) => {
    setValue(query);
    lastQueryRef.current = query;
    if (query.trim().length < MIN_SEARCH_LENGTH) {
      setResults(null);
      return;
    }
    const localities = await loadLocalities();
    // The first search waits for the data: by then the person may have typed more.
    if (lastQueryRef.current === query) setResults(searchLocalities(localities, query));
  }, []);

  const inputProps: InputProps = {
    type: 'text',
    id: 'search-address-input',
    placeholder: t('common:where_you_are_looking_for'),
    icon: MagnifyingGlassIcon,
    value,
    onChange: handleChange,
    wrapperStyle,
    className: 'rounded-full shadow-[0px_2px_8px_0px_rgba(0,0,26,0.25)]',
    hasLeftPadding: true,
    isFullwidth: true,
    testId: 'locality-search-input',
  };

  return (
    <div ref={containerRef} className="relative w-fit grid place-items-center">
      <TextInput {...inputProps} />
      {results && (
        <div
          data-cy="locality-search-results"
          className="absolute top-full mt-1 left-1/2 -translate-x-1/2 z-50 w-[300px] md:w-[315px] lg:w-[380px] xl:w-[445px] 2xl:w-[510px] rounded-lg border border-solid border-neutral-40 bg-white"
        >
          <div className="flex flex-col gap-1 p-4 border-b border-gray-200">
            <Heading as="span" size={6} weight="semibold" color="primary">
              {t('common:search_suggestions')}
            </Heading>
          </div>
          <ul className="flex flex-col gap-3 p-4 max-h-[18rem] overflow-y-auto">
            {results.length === 0 && (
              <li>
                <Heading as="span" size={6} weight="normal" color="neutral">
                  {t('common:no_results_found')}
                </Heading>
              </li>
            )}
            {results.map((locality) => {
              const label = `${locality.name}, ${locality.context}`;
              return (
                <li
                  key={label}
                  aria-label={label}
                  onClick={(event) => {
                    // The chip is a label: left alone, it clicks its checkbox and selects twice.
                    event.preventDefault();
                    setResults(null);
                    setValue('');
                    onSelect(locality);
                  }}
                >
                  <ChipCheckBox
                    Icon={MapPinIcon as unknown as IconComponentType}
                    checked={false}
                    id={label}
                    label={label}
                    wrapperStyles="w-full"
                  />
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
